import Repository from "@/repository";
import {
  getCarNumberFieldValue,
  isEmptyCarNumber,
} from "@/utils/carNumber";

export default {
  data() {
    return {
      clients: [],
      client: null,
      activeField: "",
      clientSearchField: "",
      clientSearchRequestId: 0,
      carNumberHint: "",
      prevPhoneNumberDigits: 0,
      prevPhoneNumber: "",
    };
  },
  methods: {
    normalizeTextValue(value) {
      return String(value || "")
        .trim()
        .replace(/\s+/g, " ");
    },
    normalizePhoneValue(value) {
      const raw = String(value || "");
      if (!raw.trim()) return "";
      if (raw.trim().startsWith("+")) {
        const digits = raw.replace(/[^\d]/g, "");
        return digits ? `+${digits}` : "+";
      }
      return raw.replace(/[^\d]/g, "");
    },
    normalizeCarNumberValue(value) {
      if (isEmptyCarNumber(value)) return "";
      return String(value || "")
        .toUpperCase()
        .replace(/\s+/g, "")
        .trim();
    },
    getCarNumberFieldValue,
    isEmptyCarNumber,
    resolveEntityId(value) {
      if (!value) return null;
      return typeof value === "object" ? value.id || null : value;
    },
    applyClientCarIdentity(car) {
      if (!car) return;

      this.$store.commit("orders/setOrderField", {
        field: "car.number",
        value: this.getCarNumberFieldValue(car.number),
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.make",
        value: this.resolveEntityId(car.make),
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.model",
        value: this.resolveEntityId(car.model),
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.generation",
        value: this.resolveEntityId(car.generation),
      });

      if (car.make?.title) {
        this.$store.commit("orders/setCarField", {
          field: "make",
          value: car.make.title,
        });
      }
      if (car.model?.title) {
        this.$store.commit("orders/setCarField", {
          field: "model",
          value: car.model.title,
        });
      }
      if (car.generation?.title) {
        this.$store.commit("orders/setCarField", {
          field: "generation",
          value: car.generation.title,
        });
      }
    },
    findSelectedClientCarIndex(cars, orderCar) {
      if (!Array.isArray(cars) || !cars.length || !orderCar) {
        return -1;
      }

      const byNumber = this.findClientCarByNumber(cars, orderCar.number);
      if (byNumber >= 0) {
        return byNumber;
      }

      if (this.normalizeCarNumberValue(orderCar.number)) {
        return -1;
      }

      const makeId = this.resolveEntityId(orderCar.make);
      const modelId = this.resolveEntityId(orderCar.model);
      const generationId = this.resolveEntityId(orderCar.generation);

      if (!makeId && !modelId && !generationId) {
        return -1;
      }

      return cars.findIndex((car) => {
        if (this.normalizeCarNumberValue(car?.number)) {
          return false;
        }

        return (
          this.resolveEntityId(car?.make) === makeId &&
          this.resolveEntityId(car?.model) === modelId &&
          this.resolveEntityId(car?.generation) === generationId
        );
      });
    },
    formatRuClientPhone(digits, shouldCloseBracket = true) {
      const normalizedDigits = String(digits || "")
        .replace(/\D/g, "")
        .slice(0, 10);

      let formatted = "+7";
      if (!normalizedDigits.length) {
        return formatted;
      }

      formatted += ` (${normalizedDigits.slice(0, 3)}`;
      if (!shouldCloseBracket || normalizedDigits.length < 3) {
        return formatted;
      }

      if (normalizedDigits.length === 3) {
        return `${formatted})`;
      }

      formatted += `) ${normalizedDigits.slice(3, 6)}`;
      if (normalizedDigits.length > 6) {
        formatted += `-${normalizedDigits.slice(6, 8)}`;
      }
      if (normalizedDigits.length > 8) {
        formatted += `-${normalizedDigits.slice(8, 10)}`;
      }

      return formatted;
    },
    normalizeClientPhoneInput(value) {
      const raw = String(value ?? "").trim();
      if (!raw) {
        return "+7";
      }

      if (raw.startsWith("+") && !raw.startsWith("+7")) {
        return this.normalizePhoneValue(raw);
      }

      let cleaned = raw.replace(/[^\d+]/g, "");
      if (!cleaned.startsWith("+7")) {
        if (cleaned.startsWith("7") || cleaned.startsWith("8")) {
          cleaned = `+7${cleaned.slice(1)}`;
        } else if (cleaned.startsWith("+")) {
          if (cleaned === "+") {
            return "+";
          }
          cleaned = `+7${cleaned.slice(1)}`;
        } else {
          cleaned = `+7${cleaned}`;
        }
      }

      const digits = cleaned.slice(2).replace(/\D/g, "").slice(0, 10);
      return this.formatRuClientPhone(digits);
    },
    parseClientPhoneInput(value) {
      const displayPhone = this.normalizeClientPhoneInput(value);
      if (displayPhone === "+" || displayPhone === "+7") {
        return "";
      }
      return this.normalizePhoneValue(displayPhone);
    },
    setClientPhoneInputState(value) {
      const normalizedPhone = this.normalizeClientPhoneInput(value);
      this.prevPhoneNumberDigits = normalizedPhone.replace(/[^\d]/g, "").length;
      this.prevPhoneNumber = normalizedPhone;
      return normalizedPhone;
    },
    formatCarNumberRUB(raw) {
      const plateFormat = "А999АА999";
      const allowedLetters = "АВЕКМНОРСТУХ";
      const allowedDigits = "0123456789";
      const latinToCyrillic = {
        A: "А",
        B: "В",
        E: "Е",
        K: "К",
        M: "М",
        H: "Н",
        O: "О",
        P: "Р",
        C: "С",
        T: "Т",
        Y: "У",
        X: "Х",
      };

      let value = String(raw || "").toUpperCase();
      value = value.replace(/[A-Z]/g, (char) => latinToCyrillic[char] || char);

      const invalidChars = new Set();
      let firstTypeMismatch = null;
      const expectedType = (idx) => {
        if (idx === 0 || idx === 4 || idx === 5) return "letter";
        return "digit";
      };

      let formatted = "";
      let charIndex = 0;

      for (let i = 0; i < value.length && charIndex < 9; i++) {
        const ch = value[i];
        if (ch === " " || ch === "-" || ch === "_") continue;

        const isLetter = allowedLetters.includes(ch);
        const isDigit = allowedDigits.includes(ch);

        if (!isLetter && !isDigit) {
          invalidChars.add(ch);
          continue;
        }

        const need = expectedType(charIndex);
        if (need === "letter" && !isLetter) {
          if (!firstTypeMismatch) {
            firstTypeMismatch = { pos: charIndex + 1, expected: "буква" };
          }
          continue;
        }
        if (need === "digit" && !isDigit) {
          if (!firstTypeMismatch) {
            firstTypeMismatch = { pos: charIndex + 1, expected: "цифра" };
          }
          continue;
        }

        formatted += ch;
        charIndex++;
      }

      let hint = "";
      if (invalidChars.size) {
        hint = `Недопустимые символы: ${Array.from(invalidChars).join(
          ", ",
        )}. Разрешены буквы ${allowedLetters} и цифры 0-9. Формат: ${plateFormat}.`;
      } else if (firstTypeMismatch) {
        hint = `Позиция ${firstTypeMismatch.pos}: нужна ${firstTypeMismatch.expected}. Формат: ${plateFormat}.`;
      }

      return {
        formatted,
        hint,
        hasIssues: Boolean(invalidChars.size || firstTypeMismatch),
      };
    },
    isRuClientPhone(value) {
      return String(value || "").trim().startsWith("+7");
    },
    isRuPhoneMaskComplete(value) {
      if (!this.isRuClientPhone(value)) {
        return false;
      }
      return String(value || "").replace(/\D/g, "").length >= 11;
    },
    isPhoneValidForSearch(value) {
      if (this.isRuClientPhone(value)) {
        return this.isRuPhoneMaskComplete(value);
      }
      const digits = String(value || "").replace(/\D/g, "");
      return digits.length >= 11;
    },
    getActiveClientCars(client) {
      if (!Array.isArray(client?.cars)) {
        return [];
      }
      return client.cars.filter(Boolean);
    },
    findClientCarByNumber(cars, number) {
      const normalized = this.normalizeCarNumberValue(number);
      if (!normalized || !Array.isArray(cars)) {
        return -1;
      }
      return cars.findIndex(
        (car) => this.normalizeCarNumberValue(car?.number) === normalized,
      );
    },
    formatClientCarOption(car) {
      const number = this.isEmptyCarNumber(car?.number) ? "" : car?.number;
      return [car?.make?.title, car?.model?.title, number]
        .filter(Boolean)
        .join(" ");
    },
    hasSelectedClient() {
      return Boolean(this.client?.id || this.$store.state.orders.order?.client);
    },
    isSelectedClientPhoneChanged(phone) {
      if (!this.hasSelectedClient()) {
        return false;
      }

      const selectedClientPhone = this.normalizePhoneValue(this.client?.phone_number);
      const nextPhone = this.normalizePhoneValue(phone);

      return Boolean(selectedClientPhone) && selectedClientPhone !== nextPhone;
    },
    clearSelectedClientContext({ preservePhone = "" } = {}) {
      const nextPhone = this.normalizePhoneValue(preservePhone);
      const hadOrderId = Boolean(this.$store.state.orders.order?.id);

      this.clientSearchRequestId++;
      this.client = null;
      this.clients = [];
      this.activeField = "";
      this.clientSearchField = "";
      this.$store.commit("orders/setOrderField", { field: "client", value: null });
      this.$store.commit("orders/setOrderField", {
        field: "client_data.name",
        value: null,
      });
      this.$store.commit("orders/setOrderField", {
        field: "client_data.phone_number",
        value: nextPhone || null,
      });

      if (typeof this.$emit === "function") {
        this.$emit("client-selected", null);
      }

      if (hadOrderId) {
        return this.$store.dispatch("orders/saveOrder");
      }

      return Promise.resolve({ value: null, error: null });
    },
    resetClients() {
      this.clientSearchRequestId++;
      this.clients = [];
    },
    buildFindUserParams(field) {
      const order = this.$store.state.orders.order;
      const params = {};

      if (field === "phone") {
        params.phone_number = this.normalizePhoneValue(
          order.client_data?.phone_number,
        );
      }

      return params;
    },
    canSearchByField(field) {
      const order = this.$store.state.orders.order;

      if (field === "phone") {
        return this.isPhoneValidForSearch(order.client_data?.phone_number);
      }
      return false;
    },
    applyCarNumberInput(event) {
      const current = event?.target?.value ?? "";
      const { formatted, hint, hasIssues } = this.formatCarNumberRUB(current);

      this.$store.commit("orders/setOrderField", {
        field: "car.number",
        value: formatted,
      });
      this.carNumberHint = hint;

      if (event?.target && event.target.value !== formatted) {
        event.target.value = formatted;
      }

      if (hasIssues) {
        this.resetClients();
      }
    },
    handleClientPhoneInput(event) {
      const raw = String(event?.target?.value ?? "");
      const trimmed = raw.trim();
      let normalizedPhone = "+7";

      if (trimmed.startsWith("+") && !trimmed.startsWith("+7")) {
        normalizedPhone = this.normalizePhoneValue(trimmed);
        this.prevPhoneNumberDigits = normalizedPhone.replace(/[^\d]/g, "").length;
        this.prevPhoneNumber = normalizedPhone;
      } else {
        let cleaned = trimmed.replace(/[^\d+]/g, "");
        if (!cleaned.startsWith("+7")) {
          if (cleaned.startsWith("7") || cleaned.startsWith("8")) {
            cleaned = `+7${cleaned.slice(1)}`;
          } else if (cleaned.startsWith("+")) {
            if (cleaned === "+") {
              normalizedPhone = "+";
              this.prevPhoneNumberDigits = 0;
              this.prevPhoneNumber = normalizedPhone;
            } else {
              cleaned = `+7${cleaned.slice(1)}`;
            }
          } else {
            cleaned = `+7${cleaned}`;
          }
        }

        if (normalizedPhone !== "+") {
          const digits = cleaned.slice(2).replace(/\D/g, "").slice(0, 10);
          const isDeleting = digits.length < this.prevPhoneNumberDigits;
          const hadClosingBracket = this.prevPhoneNumber.includes(")");
          const hasClosingBracket = raw.includes(")");
          const removedBracket = hadClosingBracket && !hasClosingBracket;
          const digitsIncreased = digits.length > this.prevPhoneNumberDigits;
          const shouldCloseBracket =
            digits.length >= 3 &&
            (!isDeleting || hasClosingBracket || digitsIncreased) &&
            !(removedBracket && !digitsIncreased);

          normalizedPhone = this.formatRuClientPhone(digits, shouldCloseBracket);
          this.prevPhoneNumberDigits = digits.length;
          this.prevPhoneNumber = normalizedPhone;
        }
      }

      if (event?.target && event.target.value !== normalizedPhone) {
        event.target.value = normalizedPhone;
      }

      const phone = this.parseClientPhoneInput(normalizedPhone);
      this.$store.commit("orders/setOrderField", {
        field: "client_data.phone_number",
        value: phone,
      });

      if (phone) {
        this.$store.commit("orders/clearFieldError", "client_data.phone_number");
      }

      if (this.isSelectedClientPhoneChanged(phone)) {
        const currentDraftPhone = normalizedPhone;
        const detachPromise = this.clearSelectedClientContext();
        this.$store.commit("orders/setOrderField", {
          field: "client_data.phone_number",
          value: phone || null,
        });
        Promise.resolve(detachPromise).then(() => {
          if (this.hasSelectedClient()) {
            return;
          }

          this.$store.commit("orders/setOrderField", {
            field: "client_data.phone_number",
            value: phone || null,
          });
        });
        this.prevPhoneNumberDigits = currentDraftPhone.replace(/[^\d]/g, "").length;
        this.prevPhoneNumber = currentDraftPhone;
        return currentDraftPhone;
      }

      if (!this.isPhoneValidForSearch(phone)) {
        this.resetClients();
      }

      return normalizedPhone;
    },
    triggerClientSearch(field) {
      this.activeField = field;

      if (!this.canSearchByField(field)) {
        this.resetClients();
        return;
      }

      this.findUser(field);
    },
    async findUser(field) {
      const params = this.buildFindUserParams(field);
      if (!Object.keys(params).length) {
        this.clients = [];
        return;
      }

      const requestId = ++this.clientSearchRequestId;
      const activeFieldAtRequest = field;
      const requestSignature = JSON.stringify(params);
      const { value } = await Repository.Clients.get(params);

      if (requestId !== this.clientSearchRequestId) return;
      if (activeFieldAtRequest !== this.activeField) return;
      if (requestSignature !== JSON.stringify(this.buildFindUserParams(field))) {
        return;
      }

      const clients = Array.isArray(value) ? value : value?.data || [];
      if (clients.length === 1 && field === "phone") {
        await this.setClient(clients[0], { searchField: field });
        return;
      }

      this.clients = clients;
    },
    async getFullClient(client) {
      const clientId =
        client?.id || (typeof client === "object" ? client.id : client);
      if (!clientId) return client;

      const { value, error } = await Repository.Clients.getById(clientId);
      if (error || !value) {
        return client;
      }

      return {
        ...client,
        ...value,
        cars: Array.isArray(value.cars) ? value.cars : client?.cars || [],
      };
    },
    async setClient(client, { searchField = this.activeField } = {}) {
      const fullClient = await this.getFullClient(client);
      const order = this.$store.state.orders.order;
      const hadOrderId = Boolean(order.id);
      const resolvedSearchField = searchField || this.activeField || "";

      this.$store.commit("orders/setOrderField", {
        field: "client",
        value: fullClient.id,
      });
      this.$store.commit("orders/setOrderField", {
        field: "client_data.name",
        value: fullClient.name,
      });
      this.$store.commit("orders/setOrderField", {
        field: "client_data.phone_number",
        value: this.normalizePhoneValue(fullClient.phone_number),
      });

      this.client = fullClient;
      this.clientSearchField = resolvedSearchField;
      this.clients = [];
      this.$emit("client-selected", fullClient);

      const cars = this.getActiveClientCars(fullClient);
      if (cars.length === 1) {
        this.applyClientCarIdentity(cars[0]);
      }

      if (!hadOrderId) {
        const { error } = await this.$store.dispatch("orders/createOrder");
        if (!error && typeof this.onStepOnePersisted === "function") {
          this.onStepOnePersisted();
        }
        return;
      }

      if (typeof this.onStepOneChanged === "function") {
        this.onStepOneChanged();
      }
    },
    formatClientName() {
      const order = this.$store.state.orders.order;
      const formatted = String(order.client_data?.name || "").replace(
        /(?:^|\s)\S/g,
        (char) => char.toUpperCase(),
      );
      this.$store.commit("orders/setOrderField", {
        field: "client_data.name",
        value: formatted,
      });
    },
    clearClientField(field) {
      this.clientSearchRequestId++;
      if (field === "name") {
        this.$store.commit("orders/setOrderField", {
          field: "client_data.name",
          value: "",
        });
      } else if (field === "phone") {
        this.$store.commit("orders/setOrderField", {
          field: "client_data.phone_number",
          value: "",
        });
      } else if (field === "car_number") {
        this.$store.commit("orders/setOrderField", {
          field: "car.number",
          value: "",
        });
        this.carNumberHint = "";
      }
      this.client = null;
      this.clients = [];
      this.clientSearchField = "";
      this.$store.commit("orders/setOrderField", { field: "client", value: null });
    },
  },
};

